import type { DocumentNode } from 'graphql';
import type { RequestDocument } from 'graphql-request';
import { client } from './graphql.service';

export type CustomHeaders = Record<string, string>;

export type WithHeaders<TVariables = void> = TVariables extends void
  ? { headers?: CustomHeaders }
  : TVariables & { headers?: CustomHeaders };

/**
 * Creates a queryFn with per-request headers support for RTK Query endpoints.
 * Use this with `injectEndpoints` to override generated endpoints.
 *
 * @example
 * const api = generatedApi.injectEndpoints({
 *   endpoints: (build) => ({
 *     GetRoomsForUser: build.query<GetRoomsForUserQuery, WithHeaders>({
 *       queryFn: createQueryFnWithHeaders(GetRoomsForUserDocument),
 *     }),
 *   }),
 *   overrideExisting: true,
 * });
 */
export const createQueryFnWithHeaders = <TData, TVariables = void>(
  document: RequestDocument | DocumentNode,
  defaultHeaders?: CustomHeaders,
) => {
  return async (args: WithHeaders<TVariables>) => {
    const { headers, ...variables } = (args ?? {}) as {
      headers?: CustomHeaders;
    } & Record<string, unknown>;

    try {
      const data = await client.request<TData>({
        document,
        variables: Object.keys(variables).length > 0 ? variables : undefined,
        requestHeaders: {
          ...defaultHeaders,
          ...headers,
        }, // merges with the default headers
      });
      return { data };
    } catch (error) {
      const err = error as Error;
      return {
        error: {
          name: err.name || 'Error',
          message: err.message || String(error),
          stack: err.stack || '',
        },
      };
    }
  };
};
